import React from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { UserProvider } from './UserProvider'
import { SocketProvider } from './SocketProvider'
import { StorageProvider } from './StorageProvider'
import { SnackbarProvider } from './SnackbarProvider'
import { ApiProvider } from './ApiProvider'
import { I18nextProvider } from 'react-i18next'
import { i18n } from '@/config'

// Configuration TanStack Query avec best practices
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        // Ne pas réessayer pour les erreurs 4xx
        const status = (error as { response?: { status?: number } })?.response?.status
        if (typeof status === 'number' && status >= 400 && status < 500) {
          return false
        }
        return failureCount < 3
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (remplace cacheTime)
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
      onError: (error) => {
        console.error('Mutation error:', error)
      },
    },
  },
})

// Export des providers individuels
export { UserProvider, useUser } from './UserProvider'
export { StorageProvider, useStorage, StorageKey } from './StorageProvider'
export { SnackbarProvider, useSnackbar } from './SnackbarProvider'
export { ApiProvider, useApi } from './ApiProvider'
export { SocketProvider, useSocketContext } from './SocketProvider'

// Export du queryClient et i18n
export { queryClient }
export { i18n } from '@/config'
export { I18nextProvider } from 'react-i18next'

// Interface pour les props du provider principal
interface ProvidersProps {
  children: React.ReactNode
}

// Provider principal combiné
export const Providers: React.FC<ProvidersProps> = ({ children }) => {
  return (
    <StorageProvider>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <SnackbarProvider>
            <ApiProvider>
              <UserProvider>
                <SocketProvider>
                  {children}
                </SocketProvider>
              </UserProvider>
            </ApiProvider>
          </SnackbarProvider>
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition="bottom-right"
          />
        </QueryClientProvider>
      </I18nextProvider>
    </StorageProvider>
  )
}

// Export par défaut
export default Providers
